/**
 * SocketIO Service
 *
 * Manages SocketIO connections with multiple namespaces.
 * Similar to Flutter's SocketIOConnection implementation.
 */

import { io } from 'socket.io-client';

import { SocketIONamespace } from './types';

import type { SocketIOEventData, SocketIOConnectionStatus } from './types';
import type { Socket } from 'socket.io-client';

import { config } from '@/shared/config';

// ============================================================================
// Types
// ============================================================================

type EventCallback = (event: SocketIOEventData) => void;
type StatusCallback = (status: SocketIOConnectionStatus) => void;

// ============================================================================
// SocketIO Service Class
// ============================================================================

class SocketIOService {
  private sockets: Map<string, Socket> = new Map<string, Socket>();
  private eventListeners: Set<EventCallback> = new Set<EventCallback>();
  private statusListeners: Set<StatusCallback> = new Set<StatusCallback>();
  private _status: SocketIOConnectionStatus = 'disconnected';
  private _token: string | null = null;

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  get status(): SocketIOConnectionStatus {
    return this._status;
  }

  get isConnected(): boolean {
    return this._status === 'connected';
  }

  get connectedNamespaces(): string[] {
    return Array.from(this.sockets.entries())
      .filter(([_, socket]) => socket.connected)
      .map(([namespace]) => namespace);
  }

  // --------------------------------------------------------------------------
  // Connection Management
  // --------------------------------------------------------------------------

  /**
   * Connect to all socket namespaces with authentication token
   */
  async connect(token: string): Promise<void> {
    if (this._status === 'connected' || this._status === 'connecting') {
      console.log('[SocketIO] Already connected or connecting');
      return;
    }

    this._token = token;
    this.setStatus('connecting');

    const { host, path } = config.socket;

    // Connect to all namespaces
    const namespaces = [SocketIONamespace.Notification, SocketIONamespace.User, SocketIONamespace.Call];

    try {
      await Promise.all(namespaces.map(ns => this.connectNamespace(host, path, ns, token)));
      this.setStatus('connected');
      console.log('[SocketIO] Connected to all namespaces');
    } catch (error) {
      console.error('[SocketIO] Connection error:', error);
      this.setStatus('error');
      throw error;
    }
  }

  /**
   * Connect to a specific namespace
   */
  private connectNamespace(host: string, path: string, namespace: string, token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Disconnect existing socket for this namespace
      const existingSocket = this.sockets.get(namespace);
      if (existingSocket) {
        existingSocket.disconnect();
        this.sockets.delete(namespace);
      }

      const socket = io(`${host}${namespace}`, {
        path,
        transports: ['websocket'],
        autoConnect: false,
        auth: { token },
        query: { token }, // For web compatibility
        extraHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Connection handlers
      socket.on('connect', () => {
        console.log(`[SocketIO] Connected to ${namespace}`);
        resolve();
      });

      socket.on('connect_error', error => {
        console.error(`[SocketIO] Connection error on ${namespace}:`, error.message);
        reject(error);
      });

      socket.on('disconnect', reason => {
        console.log(`[SocketIO] Disconnected from ${namespace}: ${reason}`);
        this.updateConnectionStatus();
      });

      // Handle all events
      socket.onAny((eventName: string, data: unknown) => {
        this.handleEvent(namespace, eventName, data);
      });

      this.sockets.set(namespace, socket);
      socket.connect();
    });
  }

  /**
   * Disconnect from all namespaces
   */
  disconnect(): void {
    console.log('[SocketIO] Disconnecting all sockets');

    this.sockets.forEach((socket, namespace) => {
      socket.disconnect();
      console.log(`[SocketIO] Disconnected from ${namespace}`);
    });

    this.sockets.clear();
    this._token = null;
    this.setStatus('disconnected');
  }

  /**
   * Reconnect with stored token
   */
  async reconnect(): Promise<void> {
    if (this._token) {
      await this.connect(this._token);
    }
  }

  // --------------------------------------------------------------------------
  // Event Handling
  // --------------------------------------------------------------------------

  /**
   * Handle incoming socket events
   */
  private handleEvent(namespace: string, eventName: string, data: unknown): void {
    const event: SocketIOEventData = {
      event: eventName,
      data,
      namespace,
      timestamp: new Date(),
    };

    // Notify all listeners
    this.eventListeners.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[SocketIO] Event listener error:', error);
      }
    });
  }

  /**
   * Subscribe to all socket events
   */
  subscribe(callback: EventCallback): () => void {
    this.eventListeners.add(callback);
    return () => this.eventListeners.delete(callback);
  }

  /**
   * Subscribe to connection status changes
   */
  onStatusChange(callback: StatusCallback): () => void {
    this.statusListeners.add(callback);
    // Immediately notify of current status
    callback(this._status);
    return () => this.statusListeners.delete(callback);
  }

  // --------------------------------------------------------------------------
  // Emit Events
  // --------------------------------------------------------------------------

  /**
   * Emit an event to a specific namespace
   */
  emit(namespace: SocketIONamespace, event: string, data?: unknown): void {
    const socket = this.sockets.get(namespace);
    if (socket?.connected) {
      socket.emit(event, data);
    } else {
      console.warn(`[SocketIO] Cannot emit to ${namespace}: not connected`);
    }
  }

  // --------------------------------------------------------------------------
  // Private Helpers
  // --------------------------------------------------------------------------

  private setStatus(status: SocketIOConnectionStatus): void {
    this._status = status;
    this.statusListeners.forEach(callback => {
      callback(status);
    });
  }

  private updateConnectionStatus(): void {
    const hasConnected = Array.from(this.sockets.values()).some(s => s.connected);
    if (!hasConnected && this._status === 'connected') {
      this.setStatus('disconnected');
    }
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const socketioService = new SocketIOService();
